import { Dispatch } from "redux";
import { RootState } from "..";
import {
  setBusinessList,
  setCurrentBusiness,
  addBusinessNew,
  addOrganizationNew,
  setProductList,
  setCurrentProduct,
  addProductNew,
  setAgenciesList,
  setCurrentAgency,
  addAgenciesNew,
  setOrganizationList,
  setCurrentOrganization,
  setMediaLogsList,
  setBunchBusinessList,
  setBunchProductList,
  setBunchLanguageList,
  setBunchCategoryList,
  setBunchBusinessProductMap,
  setDefaultTeamFromConstants,
  setDefaultBusinessFromConstants,
  setBunchAgenciesList,
  setBunchAllProductList,
  setBunchAllBusinessList,
  setDefaultTeam,
  setDefaultBusiness,
  setAllHostsList,
  setDenmarkHostsList,
  // addOrganisationNew,
} from "./constants.slice";
import {
  ListBusinessApi,
  UpdateBusinessApi,
  ArchiveBusinessApi,
  AddBusinessApi,
  ListProductApi,
  UpdateProductApi,
  ArchiveProductApi,
  AddProductApi,
  ListOrganizationApi,
  UpdateOrganizationApi,
  AddOrganizationApi,
  ListAgenciesApi,
  UpdateAgenciesApi,
  ArchiveAgencyApi,
  AddAgenciesApi,
  ListMediaLogsApi,
  ListBunchApi,
  GetBusinessApi,
  GetProductApi,
  ListAllHostsApi,
  ListDenmarkHostsApi,
} from "@/database";
import { getChangedValues } from "@/components/CleoPlayer/utils";

import { toast } from "../../components";

type Conflict = { field: string; id?: string; value?: any };
const getConflictMessage = (response: any, entity: string): string | null => {
  // 1. structured conflicts array
  const conflicts: Conflict[] | undefined =
    response?.data?.conflicts ||
    response?.conflicts ||
    response?.result?.conflicts;
  if (Array.isArray(conflicts) && conflicts.length > 0) {
    const field = (conflicts[0]?.field || "").toLowerCase();
    const label = field === "code" ? "code" : field === "name" ? "name" : field;
    return label
      ? `${entity} with this ${label} already exists`
      : `${entity} with this name or code already exists`;
  }
  const haystack = [
    response?.error,
    typeof response?.data === "string" ? response.data : "",
    typeof response?.result === "string" ? response.result : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (haystack.includes("unique constraint failed")) {
    if (haystack.includes("name"))
      return `${entity} with this name already exists`;
    if (haystack.includes("code"))
      return `${entity} with this code already exists`;
    return `${entity} with this name or code already exists`;
  }

  return null;
};

export const getListBusiness =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setBusinessList({ data: { isLoading: true } }));

    const response = await ListBusinessApi(params);
    dispatch(
      setBusinessList({
        data: {
          list: response.data,
          count: response.count,
          isLoading: false,
          error: "",
        },
      })
    );
  };

export const getListDenmarkHosts =
  (params: any = {}) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setDenmarkHostsList({ data: { isLoading: true } }));
    const response = await ListDenmarkHostsApi(params);
    dispatch(
      setDenmarkHostsList({
        data: {
          list: response.data,
          count: response.count,
          isLoading: false,
          error: response.error || "",
        },
      })
    );
  };

export const updateBusiness =
  (
    params: any,
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const businessList = getState().constants.businessList.list;
    const product = businessList.filter(
      (business) => business.id === params.id
    )[0];
    const finalBusinessDetails = getChangedValues(product, params);
    const finalPayload = { ...finalBusinessDetails, id: params.id };
    const response = await UpdateBusinessApi(finalPayload);
    const conflictMessage = getConflictMessage(response, "Business");
    if (!response.error && !conflictMessage) {
      dispatch(setCurrentBusiness({ ...response.data, id: params.id }));
      onSuccess?.(response.data);
    } else {
      if (conflictMessage) toast.fail(conflictMessage);
      onFail?.(conflictMessage || response.error || "");
    }
  };

export const archiveBusiness =
  (
    params: { id: string; archive: number },
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await ArchiveBusinessApi(params);
    if (!response.error) {
      const state = getState().constants;
      const listItem = state.businessList.list.find((b) => b.id === params.id);
      const base = listItem ?? state.currentBusiness;
      if (base) {
        dispatch(setCurrentBusiness({ ...base, archive: params.archive }));
      }
      onSuccess?.(response.data);
    } else {
      toast.fail("");
      onFail?.(response.error);
    }
  };

export const getBusiness =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await GetBusinessApi({ id: params.id });
    if (!response.error) {
      dispatch(setCurrentBusiness({ ...response.data, id: params.id }));
      return { success: true, data: response.data };
    } else {
      toast.fail("");
      return { success: false, error: response.error };
    }
  };

export const addBusiness =
  (
    params: any,
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const response = await AddBusinessApi(params);
      const conflictMessage = getConflictMessage(response, "Business");
      if (!response.error && !conflictMessage && response.data) {
        dispatch(addBusinessNew(response.data));
        onSuccess?.(response.data);
      } else {
        if (conflictMessage) toast.fail(conflictMessage);
        onFail?.(conflictMessage || response.error || "");
      }
    } catch (error) {
      onFail?.(error || "");
    }
  };

export const getListProduct =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setProductList({ data: { isLoading: true } }));
    const response = await ListProductApi(params);
    dispatch(
      setProductList({
        data: {
          list: response.data,
          count: response.count,
          isLoading: false,
          error: "",
        },
      })
    );
  };

function createBusinessProductMap(data: any) {
  const businessMap = data?.business?.reduce((acc: any, biz: any) => {
    acc[biz.id] = { id: biz.id, name: biz.name, data: [] };
    return acc;
  }, {});

  data?.team?.forEach((team: any) => {
    if (team.status === 1 && businessMap[team.business]) {
      businessMap[team.business].data.push({ id: team.id, name: team.name });
    }
  });

  return businessMap;
}

export const getBunchListData =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setBunchBusinessList({ data: { isLoading: true } }));
    dispatch(setBunchProductList({ data: { isLoading: true } }));
    dispatch(setBunchCategoryList({ data: { isLoading: true } }));
    dispatch(setBunchLanguageList({ data: { isLoading: true } }));
    const response = await ListBunchApi(params);

    if (response.status === 200) {
      dispatch(
        setBunchAllBusinessList({
          data: {
            list: response?.data?.all_business,
            count: response?.data?.all_business?.length,
            isLoading: false,
            error: "",
          },
        })
      );
      dispatch(
        setBunchAllProductList({
          data: {
            list: response?.data?.all_team,
            count: response?.data?.all_team?.length,
            isLoading: false,
            error: "",
          },
        })
      );
      dispatch(
        setBunchBusinessList({
          data: {
            list: response?.data?.business,
            count: response?.data?.business?.length,
            isLoading: false,
            error: "",
          },
        })
      );
      dispatch(
        setBunchCategoryList({
          data: {
            list: response?.data?.category,
            count: response?.data?.category?.length,
            isLoading: false,
            error: "",
          },
        })
      );
      dispatch(
        setBunchLanguageList({
          data: {
            list: response?.data?.language,
            count: response?.data?.language?.length,
            isLoading: false,
            error: "",
          },
        })
      );
      if (response?.data?.business && response?.data?.team) {
        const businessProductMap = createBusinessProductMap(response?.data);
        dispatch(setBunchBusinessProductMap(businessProductMap));
      }
      if (response?.data?.default_team) {
        dispatch(setDefaultTeamFromConstants(response.data.default_team));
      }
      if (response?.data?.default_business) {
        dispatch(
          setDefaultBusinessFromConstants(response.data.default_business)
        );
      }
    }
    if (response.status === 200) {
      dispatch(
        setBunchProductList({
          data: {
            list: response?.data?.team,
            count: response?.data?.team?.length,
            isLoading: false,
            error: "",
          },
        })
      );

      dispatch(
        setDefaultTeam({
          ...response?.data?.default_team,
        })
      );
      dispatch(
        setDefaultBusiness({
          ...response?.data?.default_business,
        })
      );
    }
  };

export const archiveProduct =
  (
    params: { id: string; archive: number },
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await ArchiveProductApi(params);
    if (!response.error) {
      const state = getState().constants;
      const listItem = state.productList.list.find((p) => p.id === params.id);
      const base = listItem ?? state.currentProduct;
      if (base) {
        dispatch(setCurrentProduct({ ...base, archive: params.archive }));
      }
      onSuccess?.(response.data);
    } else {
      toast.fail("");
      onFail?.(response.error);
    }
  };

export const updateProduct =
  (
    params: any,
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const productsList = getState().constants.productList.list;
    const product = productsList.filter(
      (product) => product.id === params.id
    )[0];
    const finalProductDetails = getChangedValues(product, params);
    const finalPayload = { ...finalProductDetails, id: params.id };
    const response = await UpdateProductApi(finalPayload);
    if (!response.error) {
      dispatch(setCurrentProduct({ ...response.data, id: params.id }));
      onSuccess?.(response.data);
    } else {
      if (response?.error?.includes("unique constraint failed")) {
        toast.fail("Team with this name already exists");
      } else {
        toast.fail("");
      }
    }
  };

export const getProduct =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await GetProductApi({ id: params.id });
    if (!response.error) {
      dispatch(setCurrentProduct({ ...response.data, id: params.id }));
      return { success: true, data: response.data };
    } else {
      toast.fail("");
      return { success: false, error: response.error };
    }
  };

export const addProduct =
  (
    params: any,
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const response = await AddProductApi(params);
      if (!response.error && response.data) {
        dispatch(addProductNew(response.data));
        onSuccess?.(response?.data);
      } else {
        if (response?.error?.includes("unique constraint failed")) {
          toast.fail("Team with this name already exists");
        } else {
          toast.fail("");
        }
      }
    } catch (error) {
      console.error("Product API call failed:", error);
      toast.fail((error as string) || "Failed to create product");
    }
  };

export const getListAgencies =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setAgenciesList({ data: { isLoading: true } }));
    const response = await ListAgenciesApi(params);
    if (params?.setInRedux !== false) {
      dispatch(
        setAgenciesList({
          data: {
            list: response.data,
            count: response.count,
            isLoading: false,
            error: "",
          },
        })
      );
    }
    return response.data;
  };

export const getListBunchAgencies =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setBunchAgenciesList({ data: { isLoading: true } }));
    const response = await ListAgenciesApi(params);
    if (response.status === 200) {
      dispatch(
        setBunchAgenciesList({
          data: {
            list: response.data,
            count: response.count,
            isLoading: false,
            error: "",
          },
        })
      );
    } else {
      toast.fail("");
    }
    return response.data;
  };

export const updateAgencies =
  (params: any, onClose?: () => void) => async (dispatch: Dispatch) => {
    try {
      const response = await UpdateAgenciesApi({ ...params, id: params.id });
      const conflictMessage = getConflictMessage(response, "Agency");
      if (!response.error && !conflictMessage) {
        dispatch(setCurrentAgency({ ...response.data, id: params.id }));
        onClose?.();
        toast.success("Agency updated successfully");
      } else {
        toast.fail(conflictMessage || "");
      }
    } catch (error) {
      const conflictMessage = getConflictMessage(
        { error: String(error) },
        "Agency"
      );
      toast.fail(conflictMessage || (error as string));
    }
  };

export const archiveAgency =
  (
    params: { id: string; archive: number },
    onSuccess?: (response: any) => void,
    onFail?: (error: any) => void
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await ArchiveAgencyApi(params);
    if (!response.error) {
      const state = getState().constants;
      const listItem = state.agenciesList.list.find((a) => a.id === params.id);
      const base = listItem ?? state.currentAgency;
      if (base) {
        dispatch(setCurrentAgency({ ...base, archive: params.archive } as any));
      }
      onSuccess?.(response.data);
    } else {
      toast.fail("");
      onFail?.(response.error);
    }
  };

export const addAgencies =
  (params: any, onClose?: () => void, onFail?: (error: any) => void) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const response = await AddAgenciesApi(params);
      const conflictMessage = getConflictMessage(response, "Agency");
      if (!response.error && !conflictMessage && response.data) {
        dispatch(addAgenciesNew(response.data));
        onClose?.();
        toast.success("Agency created successfully");
      } else {
        toast.fail(conflictMessage || "");
        onFail?.(
          conflictMessage || response.error || "Failed to create agency"
        );
      }
    } catch (error) {
      console.error("Agency API call failed:", error);
      onFail?.(error);
    }
  };

export const getListOrganization =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(setBusinessList({ data: { isLoading: true } }));
    const response = await ListOrganizationApi(params);
    dispatch(
      setOrganizationList({
        data: {
          list: response.data,
          count: response.count,
          isLoading: false,
          error: "",
        },
      })
    );
  };

export const updateOrganization =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    const response = await UpdateOrganizationApi(params);
    if (response.data) {
      dispatch(setCurrentOrganization({ ...response.data, id: params.id }));
    }
  };

export const addOrganization =
  (params: any, callback?: () => void) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const response = await AddOrganizationApi(params);
      if (!response.error) {
        callback?.();
        dispatch(addOrganizationNew(response.data));
      }
    } catch (error) {
      console.error("Product API call failed:", error);
    }
  };

export const getListMediaLogs =
  (params: any) => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(
      setMediaLogsList({
        data: {
          isLoading: true,
          list: [],
          count: 0,
          error: "",
          userContributorData: [],
        },
      })
    );
    const response = await ListMediaLogsApi({
      ...params,
      index: params.index || "mediameta",
      key: params.key,
    });
    dispatch(
      setMediaLogsList({
        data: {
          list: response.data,
          count: response.count,
          userContributorData: response.userContributorData ?? [],
          isLoading: false,
          error: "",
        },
      })
    );
  };
